import { ExtendableError } from '../misc';
function buildHTTPErrorMessage(status, statusText) {
    const normalizedStatusText = statusText?.trim() ?? '';
    return normalizedStatusText ? `HTTP ${status} ${normalizedStatusText}` : `HTTP ${status}`;
}
export class FetchError extends ExtendableError {
    code;
    url;
    cause;
    constructor(code, message, context = {}) {
        super(message);
        this.code = code;
        this.url = context.url;
        if (context.cause !== undefined) {
            this.cause = context.cause;
        }
    }
}
export class TimeoutError extends FetchError {
    timeoutMs;
    constructor(options = {}) {
        const message = options.timeoutMs
            ? `Request timed out after ${options.timeoutMs}ms`
            : 'Request timed out';
        super('TIMEOUT_ERROR', message, options);
        this.name = 'TimeoutError';
        this.timeoutMs = options.timeoutMs;
    }
}
export class NetworkError extends FetchError {
    constructor(cause, context = {}) {
        super('NETWORK_ERROR', 'Network request failed', { ...context, cause });
        this.name = 'NetworkError';
    }
}
export class HTTPError extends FetchError {
    status;
    statusText;
    body;
    constructor(response, body, context = {}) {
        super('HTTP_ERROR', buildHTTPErrorMessage(response.status, response.statusText), context);
        this.name = 'HTTPError';
        this.status = response.status;
        this.statusText = response.statusText;
        this.body = body;
    }
}
function normalizeStatus(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }
}
function getDefaultStatusText(status) {
    switch (status) {
        case 502:
            return 'Bad Gateway';
        case 504:
            return 'Gateway Timeout';
        default:
            return 'Upstream Request Failed';
    }
}
export function getErrorHttpStatus(error, fallbackStatus) {
    if (error instanceof HTTPError) {
        return {
            status: error.status,
            statusText: error.statusText || getDefaultStatusText(error.status),
        };
    }
    if (error instanceof TimeoutError) {
        return {
            status: 504,
            statusText: 'Gateway Timeout',
        };
    }
    if (error instanceof NetworkError) {
        return {
            status: 502,
            statusText: 'Bad Gateway',
        };
    }
    const status = normalizeStatus(error?.status) ??
        normalizeStatus(error?.statusCode) ??
        fallbackStatus;
    if (status === undefined) {
        return undefined;
    }
    const statusText = error?.statusText;
    return {
        status,
        statusText: typeof statusText === 'string' && statusText.trim()
            ? statusText
            : getDefaultStatusText(status),
    };
}
export function getErrorMessage(error, fallbackMessage = 'Unknown error') {
    const errorStatusText = getErrorHttpStatus(error)?.statusText;
    return errorStatusText || error?.message || fallbackMessage;
}
